import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { apiFetch } from "@/lib/api";
import { channelTag } from "@/lib/utmCapture";

const recentlyTracked = new Map<string, number>();
const DEDUP_MS = 5000;

// The device platform this bundle is running on — 'web' | 'ios' | 'android'.
// Tagged onto every event so the admin Launch Funnel can segment signups and
// activation by platform. Resolved once (it never changes for a session).
let PLATFORM = "web";
try { PLATFORM = Capacitor.getPlatform(); } catch { /* default web */ }

function track(event: string, page?: string, metadata?: Record<string, unknown>) {
  if (event === "page_view") {
    const key = `page_view:${page || ""}`;
    const last = recentlyTracked.get(key);
    if (last && Date.now() - last < DEDUP_MS) return;
    recentlyTracked.set(key, Date.now());
  }

  // Tag EVERY event with the first-touch campaign channel (utm_source /
  // campaign / content) so the admin Launch Funnel can attribute a signup —
  // and the activation that follows — to the channel and even the specific
  // video. Explicit metadata wins on any key collision. Empty when there's no
  // UTM (organic/direct), so it never adds noise.
  const merged = { platform: PLATFORM, ...channelTag(), ...(metadata || {}) };

  // Dual-fire: backend analytics_events table AND PostHog. The
  // backend table stays as the server-side source of truth for
  // deliberate funnels (onboarding, subscription). PostHog gets
  // the same events plus all the autocapture it's already doing,
  // so funnels on either side reference the same event names.
  apiFetch("/api/analytics/track", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event, page, metadata: merged }),
  }).catch(() => {});

  // PostHog — fire-and-forget, no-op if the key isn't set.
  import("@/lib/posthog")
    .then(({ capturePostHog }) => {
      capturePostHog(event, {
        ...(page ? { page } : {}),
        ...merged,
      });
    })
    .catch(() => {});
}

export function usePageView() {
  const location = useLocation();
  const lastPath = useRef("");

  useEffect(() => {
    if (location.pathname !== lastPath.current) {
      lastPath.current = location.pathname;
      track("page_view", location.pathname);
    }
  }, [location]);
}

export function trackEvent(event: string, metadata?: Record<string, unknown>) {
  track(event, undefined, metadata);
}

export function useOnboardingTracking() {
  return {
    trackStep: (step: number) => track("onboarding_step", undefined, { step }),
    trackComplete: () => track("onboarding_complete"),
    trackSkip: (atStep: number) => track("onboarding_skip", undefined, { atStep }),
  };
}
